import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { generateSEOMeta } from '@/components/feature/SEOMeta';
import { getWebPageSchema, getBreadcrumbSchema } from '@/components/feature/JsonLD';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [activeStatus, setActiveStatus] = useState<string>('all');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    if (!categories.find((c) => c.slug === slug)) {
      navigate(`/category/${categories[0].slug}`, { replace: true });
    }
  }, [slug, navigate]);

  const filtered = comics.filter((c) => {
    if (c.category !== currentCategory.slug) return false;
    if (activeStatus === 'ongoing' && c.status !== '连载中') return false;
    if (activeStatus === 'finished' && c.status !== '已完结') return false;
    return true;
  });

  const seo = generateSEOMeta('category', { categoryName: currentCategory.name });

  useEffect(() => {
    document.title = seo.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', seo.description);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', seo.keywords);
  }, [seo]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getWebPageSchema(seo.title, seo.description, `https://www.hanmanzhijia.com/category/${currentCategory.slug}`)
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbSchema([
              { name: '首页', url: 'https://www.hanmanzhijia.com/' },
              { name: currentCategory.name },
            ])
          ),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50">
                <i className={`${currentCategory.icon} text-xl text-primary-600`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {currentCategory.name}韩漫
              </h1>
              <span className="text-sm text-foreground-400">({filtered.length}部作品)</span>
            </div>

            <div className="flex items-center gap-2 mb-8">
              <button
                onClick={() => setActiveStatus('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeStatus === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              <button
                onClick={() => setActiveStatus('ongoing')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeStatus === 'ongoing'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                连载中
              </button>
              <button
                onClick={() => setActiveStatus('finished')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeStatus === 'finished'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                已完结
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-50 text-primary-600 border border-primary-200'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-transparent'
                  }`}
                >
                  <i className={`${cat.icon} mr-1`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="mt-4 text-foreground-500">暂无该分类的漫画作品</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                {filtered.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - ${currentCategory.name}韩漫免费读`}
                        title={`${comic.title} - 韩漫之家免费在线阅读`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            comic.status === '连载中'
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-secondary-500 text-background-50'
                          }`}
                        >
                          {comic.status}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <h3 className="mt-3 text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                      {comic.title}
                    </h3>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        <span className="text-xs text-accent-600 font-medium">{comic.rating}</span>
                      </div>
                      <span className="text-xs text-foreground-400">{comic.author}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}